/**
 * rentals_summary — surface rentals. Lifted out of the Rentals dashboard
 * (pages/rentals/SurfaceRentalsDashboard.tsx, retired 2026-10-15; git history
 * has its docblock, with the shape and the api#854 history): the asset
 * figures, the active orders, utilization by category and AR aging, all from
 * the one GET /surface-rentals/dashboard read. AR is ALL OR NOTHING: a null bucket is a dash with
 * the API's count beside it, never re-summed here.
 *
 * TODAY'S SUMMARY (AI) is back (api#1094, operator 2026-10-15): its counts are
 * the server's now, the same ones these figures read. RentalsAiSummary.tsx has
 * the history and the reads.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { unwrap } from '../../../lib/api-envelope';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { fmtCurrency } from '../../../lib/display-labels';
import { missingRateNote } from '../../../lib/missing-rate-note';
import { formatCalendarDate } from '../../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WIDGET_EYEBROW, WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';
import RentalsAiSummary from './RentalsAiSummary';

interface DashboardData {
  kpis?: {
    total_assets?: number | null;
    rented_assets?: number | null;
    available_assets?: number | null;
    overdue_orders?: number | null;
    outstanding_ar?: number | string | null;
    outstanding_ar_unknown_count?: number | null;
  };
  utilization_by_category?: { category: string | null; total: number; rented: number; utilization_pct: number }[];
  active_orders?: {
    id: string;
    order_number?: string | null;
    customer_name?: string | null;
    well_name?: string | null;
    expected_return?: string | null;
    is_overdue?: boolean;
    days_overdue?: number | string | null;
  }[];
  ar_aging?: {
    current?: string | number | null;
    past_30?: string | number | null;
    past_60?: string | number | null;
    past_90_plus?: string | number | null;
    unknown_balance_count?: number | null;
    undated_count?: number | null;
  };
}

const ORDERS_SHOWN = 5;
const INVOICES = { one: 'open invoice', many: 'open invoices' };
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const badge = 'inline-flex items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.65rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink';

export default function RentalsSummaryWidget() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['rentals-dashboard'],
    queryFn: () => api.rentals.dashboard().then((res) => (unwrap<DashboardData>(res.data) ?? {}) as DashboardData),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(q) != null;
  const data = failed ? undefined : q.data;
  const kpis = data?.kpis;
  const k = (v: number | null | undefined) => (failed || !kpis ? '—' : displayValue(v ?? null));
  const orders = data?.active_orders ?? [];
  const categories = data?.utilization_by_category ?? [];
  const aging = data?.ar_aging;
  const arNotes = [
    kpis && num(kpis.outstanding_ar) === null
      ? missingRateNote(kpis.outstanding_ar_unknown_count ?? aging?.unknown_balance_count, INVOICES, 'no balance on file')
      : null,
    aging && num(aging.current) === null ? missingRateNote(aging.undated_count, INVOICES, 'no due date') : null,
  ].filter((n): n is string => n != null);

  return (
    <WidgetPanel widgetKey="rentals_summary" eyebrow="Surface rentals" title="Rentals Summary" link={{ label: 'Rental Orders', to: '/rentals/active' }}>
      {failed ? (
        <LoadFailureNotice error={queryFailureError(q)} subject="the rentals overview" className="py-8" />
      ) : q.isLoading || !data ? (
        <WidgetPending rows={3} />
      ) : (
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-4">
            <WidgetFigure testId="rentals-assets" label="Assets" value={k(kpis?.total_assets)} sub="Total units" />
            <WidgetFigure testId="rentals-on-rent" label="On rent" value={k(kpis?.rented_assets)} sub="Out with customers" />
            <WidgetFigure testId="rentals-available" label="Available" value={k(kpis?.available_assets)} sub="Ready to go" />
            <WidgetFigure testId="rentals-overdue" label="Overdue" value={k(kpis?.overdue_orders)} sub="Past due" />
          </div>

          <RentalsAiSummary />

          {/* items-start: the orders panel is as tall as its rows. Stretched to
            * the utilization and AR column it drew an empty box under them. */}
          <div data-testid="rentals-summary-columns" className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            <section aria-labelledby="rentals-orders-title" className="min-w-0 rounded-fi-control border border-fieldiq-border">
              <h3 id="rentals-orders-title" className={`${WIDGET_EYEBROW} border-b border-fieldiq-border px-3 py-2`}>Active orders</h3>
              {orders.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-fieldiq-muted">No active rental orders</p>
              ) : (
                <ul className="divide-y divide-fieldiq-border">
                  {orders.slice(0, ORDERS_SHOWN).map((o) => (
                    <li key={o.id} data-testid="rentals-order-row" className={`border-l-4 ${o.is_overdue ? 'border-l-fi-signal-red' : 'border-l-fi-signal-green'}`}>
                      <button type="button" onClick={() => navigate('/rentals/active')} className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-fieldiq-dark/50">
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-bold text-fieldiq-text">
                            <span className="font-mono">{o.order_number ?? '—'}</span> · {o.customer_name ?? '—'}
                          </span>
                          <span className="block truncate text-xs text-fieldiq-muted">{o.well_name ?? '—'}</span>
                        </span>
                        <span className="shrink-0 text-right text-xs text-fieldiq-muted">
                          {formatCalendarDate(o.expected_return ?? null, { month: 'short', day: 'numeric' }) ?? '—'}
                          {o.is_overdue && (
                            <span className={`${badge} ml-2 bg-fi-signal-red`}>
                              {num(o.days_overdue) != null ? `${num(o.days_overdue)}d over` : 'Overdue'}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {orders.length > ORDERS_SHOWN && (
                <p className="px-3 pb-2 text-xs text-fieldiq-muted">Showing {ORDERS_SHOWN} of {orders.length}</p>
              )}
            </section>

            <div className="space-y-4">
              <section aria-labelledby="rentals-util-title" className="rounded-fi-control border border-fieldiq-border px-3 py-3">
                <h3 id="rentals-util-title" className={`${WIDGET_EYEBROW} mb-2`}>Utilization</h3>
                {categories.length === 0 ? (
                  <p className="text-sm text-fieldiq-muted">No rental equipment yet</p>
                ) : (
                  <div className="space-y-2.5">
                    {categories.map((c) => (
                      <div key={c.category ?? 'none'}>
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="text-fieldiq-text">{c.category ?? '—'}</span>
                          <span className="tabular-nums text-fieldiq-muted">
                            {c.rented} of {c.total} · <span className="font-bold text-fieldiq-text">{c.utilization_pct}%</span>
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-fieldiq-border" aria-hidden="true">
                          <div className="h-full rounded-full bg-fi-signal-blue" style={{ width: `${Math.min(100, c.utilization_pct)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section aria-labelledby="rentals-ar-title" data-testid="rentals-ar-widget" className="rounded-fi-control border border-fieldiq-border px-3 py-3">
                <h3 id="rentals-ar-title" className={`${WIDGET_EYEBROW} mb-2`}>AR aging</h3>
                <dl className="space-y-1.5 text-sm">
                  {([
                    ['Not yet due', 'current'],
                    ['1–30 days late', 'past_30'],
                    ['31–60 days late', 'past_60'],
                    ['Over 60 days late', 'past_90_plus'],
                  ] as const).map(([label, key]) => (
                    <div key={key} className="flex justify-between gap-3">
                      <dt className="text-fieldiq-muted">{label}</dt>
                      <dd className="tabular-nums text-fieldiq-text">{fmtCurrency(num(aging?.[key]))}</dd>
                    </div>
                  ))}
                  <div className="flex justify-between gap-3 border-t border-fieldiq-border pt-1.5 font-bold">
                    <dt className="text-fieldiq-text">Outstanding</dt>
                    <dd className="tabular-nums text-fieldiq-text">{fmtCurrency(num(kpis?.outstanding_ar))}</dd>
                  </div>
                </dl>
                {arNotes.length > 0 && (
                  <div className="mt-2 border-t border-fieldiq-border pt-2 text-xs text-fieldiq-text">
                    {arNotes.map((n) => <p key={n}>{n}</p>)}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </WidgetPanel>
  );
}
